'use client';

import React, { useMemo, useRef } from 'react';

import { useStoreRedirect } from '@/hooks/useStoreRedirect';
import { ICtaItem } from '@/lib/types';
import { cn } from '@/lib/utils';
import { waitForTracking } from '@/lib/waitForTracking';

import { ImageItem } from './Media/Image';
import { useIsMobileDevice } from '@/hooks/useIsMobileDevice';

interface ICtaItemProps extends ICtaItem {
  uniqueIdentifier: string;
  className?: string;
  onBlockClick?: () => void | Promise<void>;
}

export const CtaItem: React.FC<ICtaItemProps> = ({
  text,
  redirectUrl,
  assetUrl,
  assetUrlMobile,
  isStoreRedirect,
  uniqueIdentifier,
  className,
  onBlockClick,
}) => {
  const itemRef = useRef<HTMLDivElement>(null);
  const { redirect } = useStoreRedirect();
  const isMobile = useIsMobileDevice();

  const imgUrl = useMemo(() => {
    return isMobile && assetUrlMobile ? assetUrlMobile : assetUrl;
  }, [assetUrl, assetUrlMobile, isMobile]);

  const handleClick = async () => {
    if (!redirectUrl) return;

    // Track the UI event before redirection. The redirect replaces the page, and
    // WebKit cancels in-flight requests when that happens, so give the request
    // a moment to finish (capped, never blocks the redirect on failure).
    await waitForTracking(onBlockClick?.());

    await redirect(redirectUrl, isStoreRedirect);
  };

  return (
    <div
      ref={itemRef}
      onClick={handleClick}
      className={cn(
        'cta-item relative flex cursor-pointer items-center justify-center rounded-xl bg-cover bg-center bg-no-repeat shadow-post-shadow transition-opacity hover:opacity-90',
        "text-center text-xl font-black leading-6 text-white [font-feature-settings:'liga'_off,'clig'_off]",
        imgUrl
          ? 'shrink-1 grow-0 basis-auto justify-center self-center'
          : 'h-[54px] w-[365px] bg-[#71717A]',
        className
      )}
    >
      <div data-testid="BlockItem-Play-Now-Text" className="absolute px-[18px] py-[15px]">
        {text}
      </div>
      {/* Parent onClick only — nested img onClick would double-fire the async redirect. */}
      {imgUrl && <ImageItem assetUrl={imgUrl} />}
    </div>
  );
};
